/**
 * Multi-image uploader backed by object storage (R2 via the API).
 *
 * Value is an array of public URL strings — wire it straight into a Form.Item
 * (e.g. a lead's `attachments`). Each pick uploads to the bucket and appends
 * the returned URL; removing a tile drops it from the array (and best-effort
 * deletes the object if we can recover its key from the URL).
 *
 * Usage (inside an antd Form):
 *   <Form.Item name="attachments"><MultiImageUploader folder="leads" /></Form.Item>
 */
import { useState } from 'react'
import { Upload, message } from 'antd'
import type { UploadFile } from 'antd/es/upload/interface'
import { PlusOutlined, LoadingOutlined } from '@ant-design/icons'
import type { UploadFolder } from '@/services/api'
import { api } from '@/services/api'

interface Props {
  folder: UploadFolder
  value?: string[]
  onChange?: (urls: string[]) => void
  maxCount?: number
}

export default function MultiImageUploader({ folder, value = [], onChange, maxCount = 10 }: Props) {
  const [loading, setLoading] = useState(false)

  const fileList: UploadFile[] = value.map((url, i) => ({
    uid: `${i}-${url}`,
    name: url.split('/').pop() || `file-${i}`,
    status: 'done',
    url,
  }))

  const handleUpload = async (file: File) => {
    if (value.length >= maxCount) {
      message.warning(`Up to ${maxCount} files`)
      return false
    }
    setLoading(true)
    try {
      const { url } = await api.uploadImage(folder, file)
      onChange?.([...value, url])
    } catch (err: any) {
      message.error(err?.message || 'Upload failed')
    } finally {
      setLoading(false)
    }
    return false // prevent antd's own request
  }

  const handleRemove = (file: UploadFile) => {
    const url = file.url || ''
    onChange?.(value.filter((u) => u !== url))
    // Best-effort delete from the bucket; backend extracts the key from the URL.
    if (url) void api.deleteImage(url).catch(() => {})
    return true
  }

  return (
    <Upload
      listType="picture-card"
      accept="image/*"
      fileList={fileList}
      beforeUpload={(file) => handleUpload(file as unknown as File)}
      onRemove={handleRemove}
    >
      {value.length >= maxCount ? null : (
        <div>
          {loading ? <LoadingOutlined /> : <PlusOutlined />}
          <div style={{ marginTop: 8 }}>Upload</div>
        </div>
      )}
    </Upload>
  )
}
